import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api, type PushConfigResponse } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { pushToast } from "@/lib/toast";

/**
 * M26c — the browser side of web push, extracted from `PushDevices` so a second surface
 * (the enable-push banner on /notifications) can share it without duplicating the one piece
 * of this that is genuinely tricky: not lying about a permission that lives in the browser.
 *
 * Everything below moved verbatim from PushDevices (M23d); that component is now a consumer.
 * Its tests passing unchanged is the proof this extraction changed nothing.
 *
 * The subscribe call must happen inside a click. `Notification.requestPermission()` is
 * gated on a user gesture, and a browser that refuses a programmatic prompt does so by
 * resolving "denied" — indistinguishable from the user having said no, and permanent.
 */

/** Decided at module load, exactly as before — jsdom-based tests stub the globals first. */
const supported =
  typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof window !== "undefined" && "PushManager" in window;

/**
 * VAPID keys travel as base64url; `applicationServerKey` wants the raw bytes.
 *
 * Backed by an explicit `ArrayBuffer` rather than `Uint8Array.from`, whose inferred
 * `ArrayBufferLike` admits `SharedArrayBuffer` and is therefore not a `BufferSource`.
 */
function decodeKey(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = (base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export type PushSubscriptionState = {
  /** Whether this browser can do web push at all (iOS: only as an installed PWA). */
  supported: boolean;
  /** True once the service worker has answered whether a subscription exists — render nothing push-related before it. */
  checked: boolean;
  /** Permission refused at the browser level; the app cannot re-ask. */
  denied: boolean;
  /** This browser's own subscription endpoint, which only the browser can answer for. */
  endpoint: string | null;
  config: UseQueryResult<PushConfigResponse>;
  subscribe: UseMutationResult<string, Error, void>;
  unsubscribe: UseMutationResult<void, Error, void>;
};

export function usePushSubscription(): PushSubscriptionState {
  const queryClient = useQueryClient();
  const config = useQuery({ queryKey: queryKeys.pushConfig, queryFn: api.pushConfig });

  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [checked, setChecked] = useState(!supported);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        if (cancelled) return;
        setEndpoint(subscription?.endpoint ?? null);
        setChecked(true);
      })
      .catch(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const subscribe = useMutation({
    mutationFn: async () => {
      const publicKey = config.data?.publicKey;
      if (!publicKey) throw new Error("The server has no VAPID key.");

      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Notification permission was not granted.");

      const registration = await navigator.serviceWorker.ready;
      // `userVisibleOnly` is required by every browser that implements this; a push that
      // shows nothing is exactly the abuse the flag exists to prevent.
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeKey(publicKey),
      });

      const json = subscription.toJSON();
      await api.subscribePush({
        endpoint: subscription.endpoint,
        keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
      });
      return subscription.endpoint;
    },
    onSuccess: (value) => {
      setEndpoint(value);
      void queryClient.invalidateQueries({ queryKey: queryKeys.pushConfig });
      pushToast("This device will be notified.");
    },
    onError: (error) => pushToast(error instanceof Error ? error.message : "Could not subscribe."),
  });

  const unsubscribe = useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      // Tell the server even if the browser had already dropped it, so the row goes too.
      const target = subscription?.endpoint ?? endpoint;
      if (subscription) await subscription.unsubscribe();
      if (target) await api.unsubscribePush(target);
    },
    onSuccess: () => {
      setEndpoint(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.pushConfig });
      pushToast("This device will no longer be notified.");
    },
    onError: (error) => pushToast(error instanceof Error ? error.message : "Could not unsubscribe."),
  });

  const denied = supported && typeof Notification !== "undefined" && Notification.permission === "denied";

  return { supported, checked, denied, endpoint, config, subscribe, unsubscribe };
}
